/**
 * Parses the "prefill" parameters that email links add to a donation page's
 * address (IJM Netherlands). Pure functions, no DOM: the donation form applies
 * the result (donation-form.js), and url-prefill.test.mjs tests it.
 *
 *   ?email=…&vnaam=…&tv=…&anaam=…       prefill email, first name, name prefix
 *                                        (tussenvoegsel), last name
 *   &amounts=12,35,63,128                replace the suggested one-time amounts
 *   &monthly-amounts=5,15,83,96          replace the suggested monthly amounts
 *   &annual-amounts=…                    replace the suggested annual amounts
 *   &amount=75                           the amount to start with
 *   &frequency=monthly                   single | monthly | annual(ly)
 *   &campaign=XYZ                        overrides the form's campaign ID
 *
 * Anything missing or invalid is returned as null and ignored, so a bad link
 * never breaks the form. Values are only ever assigned to input values or to
 * the form's settings, never written into the page as HTML.
 */

export const MIN_AMOUNT = 1;
export const MAX_AMOUNT = 250000; // same ceiling as donationWidget.twig / Data.php
export const MAX_BUTTONS = 6;

const CAMPAIGN_RE = /^[A-Za-z0-9_-]{1,40}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** "12,35,63,128" -> [12, 35, 63, 128]; whole euros only, 1..250000, no duplicates, at most 6. */
export function parseAmountList(value) {
	if (typeof value !== 'string') return null;
	const seen = new Set();
	const list = [];
	for (const part of value.split(',')) {
		const t = part.trim();
		if (!/^\d+$/.test(t)) continue;
		const n = Number(t);
		if (n < MIN_AMOUNT || n > MAX_AMOUNT || seen.has(n)) continue;
		seen.add(n);
		list.push(n);
		if (list.length === MAX_BUTTONS) break;
	}
	return list.length ? list : null;
}

/** "75", "75.50" or "75,50" -> number between 1 and 250000, else null. */
export function parseAmount(value) {
	if (typeof value !== 'string') return null;
	const t = value.trim().replace(',', '.');
	if (!/^\d+(\.\d{1,2})?$/.test(t)) return null;
	const n = Number(t);
	return n >= MIN_AMOUNT && n <= MAX_AMOUNT ? n : null;
}

/** single | monthly | annual (also accepts "annually") -> the form's recurrence key, else null. */
export function parseFrequency(value) {
	if (typeof value !== 'string') return null;
	switch (value.trim().toLowerCase()) {
		case 'single': return 'single';
		case 'monthly': return 'monthly';
		case 'annual':
		case 'annually': return 'annual';
		default: return null;
	}
}

/**
 * Trimmed text without control characters, cut to maxLength; null when empty.
 * Anything containing < or > is rejected outright: no real name or address has
 * them, and it keeps markup out of the form (and out of Salesforce).
 */
export function parseText(value, maxLength) {
	if (typeof value !== 'string') return null;
	const t = value.replace(/[\u0000-\u001f\u007f]/g, '').trim().slice(0, maxLength);
	return t === '' || /[<>]/.test(t) ? null : t;
}

export function parseEmail(value) {
	const t = parseText(value, 255);
	return t !== null && EMAIL_RE.test(t) ? t : null;
}

export function parseCampaign(value) {
	if (typeof value !== 'string') return null;
	const t = value.trim();
	return CAMPAIGN_RE.test(t) ? t : null;
}

/** @param {string} search e.g. window.location.search */
export function parseDonationUrlParams(search) {
	const q = new URLSearchParams(search || '');
	return {
		email: parseEmail(q.get('email')),
		firstName: parseText(q.get('vnaam'), 40),
		lastNamePrefix: parseText(q.get('tv'), 20),
		lastName: parseText(q.get('anaam'), 80),
		singleAmounts: parseAmountList(q.get('amounts')),
		monthlyAmounts: parseAmountList(q.get('monthly-amounts')),
		annualAmounts: parseAmountList(q.get('annual-amounts')),
		amount: parseAmount(q.get('amount')),
		frequency: parseFrequency(q.get('frequency')),
		campaign: parseCampaign(q.get('campaign')),
	};
}
